import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Clock, Check, X, Sparkles, Lightbulb, AlertTriangle, ArrowRight } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Seo from '@/components/shared/Seo';
import { QuestionPassage, QuestionTableData, QuestionStem, AnswerOptionList, EXAM_TEXT_CLS } from '@/components/exam/QuestionDisplay';
import { gmatApi, goToGoogleAuth } from '@/lib/gmatApi';
import { toast } from 'sonner';
import WinhouseAIFeedbackBadge from '@/components/shared/WinhouseAIFeedbackBadge';

const SECTIONS = [
  { key: 'quant', label: 'Quantitative' },
  { key: 'verbal', label: 'Verbal' },
  { key: 'data_insights', label: 'Data Insights' },
];

const GmatPracticePage = () => {
  const [params] = useSearchParams();
  const initialSection = SECTIONS.find((s) => s.key === params.get('section'))?.key || 'quant';
  const initialTopic = params.get('topic') || '';

  const [section, setSection] = useState(initialSection);
  const [topic, setTopic] = useState(initialTopic);
  const [difficulty, setDifficulty] = useState('');
  const [timed, setTimed] = useState(true);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [mobile, setMobile] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const startedAt = useRef(null);

  useEffect(() => {
    gmatApi.authMe()
      .catch((e) => { if (e.status === 401) setNeedsLogin(true); })
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    if (!question || result) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [question, result]);

  const loadNext = async () => {
    setLoading(true);
    setSelected(null);
    setResult(null);
    setSeconds(0);
    startedAt.current = Date.now();
    try {
      const res = await gmatApi.practiceNext({ section, topic: topic || undefined, difficulty: difficulty || undefined, timed });
      setQuestion(res.question);
    } catch (e) {
      if (e.status === 401) setNeedsLogin(true);
      else toast.error(e.message || 'Could not load question');
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!selected) return toast.info('Select an answer first');
    try {
      const r = await gmatApi.practiceSubmit({ question_id: question.id, selected_option: selected, seconds_spent: seconds });
      setResult(r);
    } catch (e) {
      toast.error(e.message || 'Submit failed');
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <Loader2 className="h-7 w-7 animate-spin text-[#007A7A]" />
      </div>
    );
  }

  if (needsLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-5 w-5 text-[#D3D86F]" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Sign in to practice</h1>
            <p className="text-sm text-gray-600 mb-6">Adaptive questions tailored to your performance.</p>
            <Button onClick={() => goToGoogleAuth('/gmat/practice')} className="w-full bg-black hover:bg-gray-900 text-white rounded-full h-11">
              Sign in with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="gmat-practice">
      <Seo title="GMAT Practice · Winhouse" canonical="/gmat/practice" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />

      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-10 w-full">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#007A7A] font-semibold mb-1.5">Adaptive Practice</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Practice Mode</h1>
          <p className="text-gray-600 text-sm mt-1">Difficulty adjusts after every answer. Get instant solutions.</p>
        </div>

        {/* Filter card */}
        <Card className="mb-5 border-gray-200">
          <CardContent className="p-4 sm:p-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Section</label>
              <select
                className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#007A7A]/20 focus:border-[#007A7A]"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                data-testid="prac-section"
              >
                {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Topic (optional)</label>
              <input
                className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#007A7A]/20 focus:border-[#007A7A]"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. algebra"
                data-testid="prac-topic"
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Difficulty</label>
              <select
                className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#007A7A]/20 focus:border-[#007A7A]"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                data-testid="prac-difficulty"
              >
                <option value="">Adaptive</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <label className="text-sm flex items-center gap-2 self-center text-gray-700">
              <input type="checkbox" className="accent-[#007A7A]" checked={timed} onChange={(e) => setTimed(e.target.checked)} /> Timed
            </label>
            <Button
              onClick={loadNext}
              className="ml-auto bg-[#007A7A] hover:bg-[#005f5f] text-white rounded-full h-11 px-5"
              data-testid="prac-next"
            >
              {question ? 'Next question' : 'Start practice'} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-[#007A7A]" />
          </div>
        )}

        {question && !loading && (
          <Card key={question.id} className="border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CardContent className="p-6 sm:p-8">
              {/* Question header */}
              <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-black text-white capitalize">{question.section.replace('_', ' ')}</Badge>
                  <Badge variant="outline" className="capitalize">{question.topic.replace(/_/g, ' ')}</Badge>
                  <Badge variant="outline" className="capitalize">{question.difficulty}</Badge>
                </div>
                {timed && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-mono text-gray-700 bg-gray-100 rounded-full px-3 py-1">
                    <Clock className="h-3.5 w-3.5" /> {seconds}s
                  </span>
                )}
              </div>

              {question.passage_text && <QuestionPassage text={question.passage_text} />}
              {question.table_or_chart_data && <QuestionTableData text={question.table_or_chart_data} />}

              <QuestionStem text={question.question_text} testId="prac-question-text" />

              <AnswerOptionList
                question={question}
                selectedLetter={selected}
                result={result}
                onSelect={(k) => setSelected(k)}
                testIdPrefix="prac-opt"
              />

              {!result && (
                <Button
                  onClick={submit}
                  disabled={!selected}
                  className="w-full bg-black hover:bg-gray-900 text-white rounded-full h-12 text-base disabled:opacity-40"
                  data-testid="prac-submit"
                >
                  Submit Answer
                </Button>
              )}

              {/* Solution box */}
              {result && (
                <div
                  className="mt-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
                  data-testid="prac-explanation"
                >
                  <div
                    className={`rounded-xl p-5 mb-5 ${
                      result.is_correct
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {result.is_correct ? (
                        <Check className="h-5 w-5 text-green-700" />
                      ) : (
                        <X className="h-5 w-5 text-red-700" />
                      )}
                      <span className={`font-bold ${result.is_correct ? 'text-green-800' : 'text-red-800'}`}>
                        {result.is_correct
                          ? 'Correct'
                          : `Incorrect — answer is ${result.correct_answer.toUpperCase()}`}
                      </span>
                    </div>
                    <p className={`text-sm ${result.is_correct ? 'text-green-900/80' : 'text-red-900/80'}`}>
                      {result.is_correct ? 'Nicely done. Lock in the reasoning below.' : 'Review the solution to lock the concept.'}
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-5 mb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-[#D3D86F]" />
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Solution</span>
                      </div>
                      <WinhouseAIFeedbackBadge variant="inline" />
                    </div>
                    <p className={`text-sm sm:text-base text-gray-800 ${EXAM_TEXT_CLS}`}>{result.full_explanation}</p>
                  </div>

                  {result.trap_explanation && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-5">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-amber-700" />
                        <span className="text-xs uppercase tracking-wider text-amber-800 font-semibold">Watch out for</span>
                      </div>
                      <p className={`text-sm text-amber-900 ${EXAM_TEXT_CLS}`}>{result.trap_explanation}</p>
                    </div>
                  )}

                  <Button
                    onClick={loadNext}
                    className="w-full bg-[#007A7A] hover:bg-[#005f5f] text-white rounded-full h-12"
                    data-testid="prac-next-after"
                  >
                    Next question <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!question && !loading && (
          <Card className="border-dashed border-2 border-gray-200">
            <CardContent className="p-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-[#E6F4F4] flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-5 w-5 text-[#007A7A]" />
              </div>
              <p className="text-gray-700 font-semibold mb-1">Ready when you are.</p>
              <p className="text-sm text-gray-500">Pick a section and tap "Start practice" to begin.</p>
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default GmatPracticePage;
